import { Button, Drawer, Form, Input, Select, Space, Tag, Typography, message } from 'antd';
import { CSSProperties } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectSessionID } from '../../features/analysisConfig/analysisConfigSlice';
import { selectVisibleAnnotations, setVisibleAnnotations } from '../../features/analysisConfig/sessionInfoSlice';
import { RunMetadata } from '../../types/runsType';
import { renderTimestamp } from '../../compute/utils';
import { fetchAuthorizedAPIEndpoint } from '../../utils';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { Annotation, AnnotationScope, AnnotationTargetType, AnnotationType } from '../../types/annotationTypes';

const annotationTypeOptions = [
  { value: AnnotationType.Note, label: 'Add a note / comment' },
  { value: AnnotationType.Correction, label: 'Correct a mistake' },
];

const annotationScopeOptions = [
  { value: AnnotationScope.Session, label: 'This session', description: 'Annotation will be locally to this session only' },
  { value: AnnotationScope.Run, label: 'A run', description: 'Annotation will be persisted to any session that load the selected run' },
];

const runTargetTypeOptions = [
  { value: null, label: 'The entire run' },
  { value: AnnotationTargetType.Name, label: 'Name' },
  { value: AnnotationTargetType.Spotfile, label: 'Spotfile' },
  { value: AnnotationTargetType.Item, label: 'Item' },
  { value: AnnotationTargetType.Tag, label: 'Tag' },
];

const sessionTargetTypeOptions = [
  { value: null, label: 'The entire session' },
  { value: AnnotationTargetType.Record, label: 'Record' },
  { value: AnnotationTargetType.Signature, label: 'Signature' },
  { value: AnnotationTargetType.PCA, label: 'PCA' },
  { value: AnnotationTargetType.DistanceMatrix, label: 'Distance Matrix' },
];

// const pcaOptions = [
//   { value: 'session', label: 'Session PCA' },
//   { value: 'reference', label: 'Reference PCA' },
//   { value: 'reference-no-amine', label: 'Reference PCA (No Amine)' },
// ];

const containerStyle: CSSProperties = {
  position: 'relative',
  height: '100vh',
  overflow: 'hidden',
};

const DashboardAnnotations: React.FC<{ runs: RunMetadata[] }> = ({ runs }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const dispatch = useAppDispatch();
  const sessionID = useAppSelector(selectSessionID);
  const visibleAnnotations = useAppSelector(selectVisibleAnnotations);
  const [accessToken, setAccessToken] = useState<string>();
  const [annotationType, setAnnotationType] = useState<AnnotationType>(AnnotationType.Note);
  const [annotationScope, setAnnotationScope] = useState<AnnotationScope>(AnnotationScope.Session);
  const [annotationTargetType, setAnnotationTargetType] = useState<AnnotationTargetType | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    getAccessTokenSilently().then((token) => setAccessToken(token));
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (accessToken === undefined) {
      return;
    }

    // FIXME : implement a better access control to annotations
    // for now session_id is used for access validation as for now annotations are only accessible from a DOH session
    fetchAuthorizedAPIEndpoint(`/annotations/session?session_id=${sessionID}`, accessToken, {
      method: 'GET',
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw new Error();
        }
      })
      .then((annotations: Annotation[] | null) => {
        if (annotations === null) {
          annotations = [];
        }
        setAnnotations(annotations);
      })
      .catch((e: Error) => {
        message.error('Failed to fetch annotations for this session. Please try again later');
      });
  }, [accessToken]);

  const handleAnnotationTypeChange = (value: AnnotationType) => {
    setAnnotationType(value);
  };

  const handleScopeChange = (value: AnnotationScope) => {
    setAnnotationScope(value);
    if (value === AnnotationScope.Session) {
      form.setFieldsValue({ annotationScopeID: sessionID });
      setAnnotationTargetType(null);
    } else {
      form.setFieldsValue({ annotationScopeID: undefined });
      setAnnotationTargetType(null);
    }
  };

  const handleTargetTypeChange = (value: AnnotationTargetType) => {
    setAnnotationTargetType(value);
  };

  const handleSubmit = (values: { annotationScopeID: string; content: string }) => {
    const authorName = user?.name || user?.email || 'You';
    let newAnnotation: Annotation = {
      type: annotationType,
      scope: annotationScope,
      scopeID: values.annotationScopeID,
      target: annotationTargetType !== null ? { type: annotationTargetType } : undefined,
      content: values.content,
      authorName: authorName,
      createdAt: new Date().toISOString(),
    };

    // FIXME : implement a better access control to annotations
    // for now session_id is used for access validation as for now annotations are only accessible from a DOH session
    fetchAuthorizedAPIEndpoint(`/annotations?session_id=${sessionID}`, accessToken, {
      method: 'POST',
      body: JSON.stringify(newAnnotation),
    }).then((resp) => {
      if (resp.ok) {
        message.success('Successfully saved annotation !');
        setAnnotations((currentAnnotations) => [...currentAnnotations, newAnnotation]);
      } else {
        message.error('Failed to save annotation. Please try again later');
      }
    });

    form.resetFields(['content']);
  };

  const formatRunLabel = (runUID: string) => {
    const selectedRun = runs.find((x) => x.UID === runUID);
    return selectedRun !== undefined ? `${selectedRun.Name} (${selectedRun.ID})` : '';
  };

  return (
    <div style={{ ...containerStyle, width: visibleAnnotations ? 400 : 0, padding: visibleAnnotations ? 48 : 0, textAlign: 'center' }}>
      <Drawer
        title="Annotations"
        placement="right"
        open={visibleAnnotations}
        getContainer={false}
        width={400}
        styles={{ body: { overflowY: 'auto', textAlign: 'left' }, header: { borderBottom: 'none' } }}
        style={{ position: 'fixed', right: 0, top: 50, width: visibleAnnotations ? 400 : 0, height: 'calc(100% - 50px)' }}
        mask={false}
        onClose={() => dispatch(setVisibleAnnotations(false))}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="What kind of annotation?" required>
            <Select value={annotationType} options={annotationTypeOptions} onChange={handleAnnotationTypeChange} />
          </Form.Item>
          <Form.Item label="Which scope for the annotation ?" required extra={annotationScope === AnnotationScope.Session ? 'Annotation is scoped to this session only' : 'Annotation is persisted to any future session that load the run'}>
            <Select value={annotationScope} options={annotationScopeOptions} onChange={handleScopeChange} />
          </Form.Item>
          <Form.Item name="annotationScopeID" initialValue={sessionID} label={`Which ${AnnotationScope[annotationScope].toLowerCase()} to annotate?`} hidden={annotationScope !== AnnotationScope.Run} required>
            <Select placeholder="Select an object" options={annotationScope === AnnotationScope.Run ? runs.map((run) => ({ value: run.UID, label: `${run.Name} (${run.ID})` })) : [{ value: sessionID, label: 'This session' }]} />
          </Form.Item>
          <Form.Item label={`What to annotate ?`} required>
            <Select value={annotationTargetType} options={annotationScope === AnnotationScope.Run ? runTargetTypeOptions : sessionTargetTypeOptions} onChange={handleTargetTypeChange} />
          </Form.Item>
          <Form.Item name="content" label="What is the annotation content?" rules={[{ required: true, whitespace: true, message: 'Enter annotation content' }]}>
            {annotationTargetType === AnnotationTargetType.Spotfile && annotationType === AnnotationType.Correction ? (
              <Input placeholder="Enter corrected spotfile name.." addonAfter={'.fsp64.spotsgrid.csv'}></Input>
            ) : (
              <Input.TextArea rows={5} placeholder={'Add context about this object'} />
            )}
          </Form.Item>
          <Button type="primary" htmlType="submit">
            Add annotation
          </Button>
        </Form>

        {annotations.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: 28 }}>
              Annotations found
            </Typography.Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {annotations
                .slice()
                .sort((a, b) => (new Date(b.createdAt) > new Date(a.createdAt) ? 1 : -1))
                .map((annotation, index) => (
                  <div key={`${annotation.scope}-${index}`} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <Typography.Text strong>{annotation.authorName}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {renderTimestamp(annotation.createdAt)}
                      </Typography.Text>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Tag>{annotation.scope === AnnotationScope.Run ? 'RUN' : 'SESSION'}</Tag>
                      <Typography.Text type="secondary">{annotation.scope === AnnotationScope.Run ? formatRunLabel(annotation.scopeID) : annotation.scopeID}</Typography.Text>
                    </div>
                    <Typography.Paragraph style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{annotation.content}</Typography.Paragraph>
                  </div>
                ))}
            </Space>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default DashboardAnnotations;
